// import { zlibSync } from "fflate";
import { zlibSync } from "../zlib";
import { CRC32 } from "./crc";

export class PNGEncoder {
  private readonly view: DataView;

  private crc32 = new CRC32();

  constructor(private readonly width: number, private readonly height: number) {
    this.view = new DataView(new ArrayBuffer(4 * width * height));
  }

  private createChunk(type: string, data: Uint8Array): Uint8Array {
    // 长度（4字节，大端序）
    const length = new Uint8Array(4);
    new DataView(length.buffer).setUint32(0, data.length, false);

    // 块类型（4字节， ASCII）
    const chunkType = Uint8Array.from(type, c => c.charCodeAt(0));

    // 计算 CRC32 校验（类型 + 数据）
    const partialChunk = new Uint8Array(chunkType.length + data.length);
    partialChunk.set(chunkType);
    partialChunk.set(data, chunkType.length);
    
    const crc = new Uint8Array(4);
    new DataView(crc.buffer).setUint32(0, this.crc32.calculate(partialChunk) >>> 0, false);

    // 汇总成完整的chunk数据
    const result = new Uint8Array(length.length + partialChunk.length + crc.length);
    result.set(length);
    result.set(partialChunk, length.length);
    result.set(crc, length.length + partialChunk.length);

    return result;
  }

  private createIHDRChunk(): Uint8Array {
    const ihdrData = new Uint8Array(13);
    const view = new DataView(ihdrData.buffer);

    // 宽度
    view.setUint32(0, this.width, false);
    // 高度
    view.setUint32(4, this.height, false);
    // 位深度
    view.setUint8(8, 8);
    // 颜色类型
    view.setUint8(9, 6);
    // 压缩方法
    view.setUint8(10, 0);
    // 过滤器方法
    view.setUint8(11, 0);
    // 交错方法
    view.setUint8(12, 0);

    return this.createChunk("IHDR", ihdrData);
  }

  private createIDATChunk(): Uint8Array {
    const { width, height } = this;
    const validRowSize = width * 4;
    // 每行开头添加一位过滤头数据
    const rowSize = validRowSize + 1;
    const data = new Uint8Array(rowSize * height);
    // 将Uint32数据转换为Uint8数据
    const pixelsData = new Uint8Array(this.view.buffer);
    let startIdx: number;
    let srcStart: number;

    for (let y = 0; y < height; y++) {
      startIdx = y * rowSize;
      data[startIdx] = 0x00; // 过滤头
      // ✅ 复制预先转换好的 RGBA 数据
      srcStart = y * validRowSize; // Uint32 => 每个元素占 4 字节
      data.set(pixelsData.subarray(srcStart, srcStart + validRowSize), startIdx + 1);
    }

    // 使用 zlib 进行压缩, 平衡压缩率有利于提升文件生成速度
    return this.createChunk("IDAT", zlibSync(data));
  }

  public setPixel(x: number, y: number, pixel: number): void {
    this.view.setUint32((y * this.width + x) * 4, pixel, false);
  }

  public write(pixels: Uint8Array | Uint8ClampedArray): this {
    const { width, height } = this;
    let pos: number;
    let r: number;
    let g: number;
    let b: number;
    let a: number;
    let pixel: number;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        pos = (y * width + x) * 4;
        r = pixels[pos];
        g = pixels[pos + 1];
        b = pixels[pos + 2];
        a = pixels[pos + 3];
        pixel = ((r << 24) | (g << 16) | (b << 8) | a) >>> 0;
        this.setPixel(x, y, pixel);
      }
    }

    return this;
  }

  public flush(): Uint8Array {
    // 预先创建所有块
    const iHDRChunk = this.createIHDRChunk();
    const iDATChunk = this.createIDATChunk();
    const iENDChunk = this.createChunk("IEND", new Uint8Array(0));
    
    // 一次性分配内存（直接计算总大小）
    const pngData = new Uint8Array(8 + iHDRChunk.length + iDATChunk.length + iENDChunk.length);
    
    /* ------ 按顺序写入数据 ------ */

    // 1. 写入文件头（固定 8 字节）
    let offset = 0;
    pngData.set(new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]), offset);
    // 2. 写入IHDR块
    offset += 8;
    pngData.set(iHDRChunk, offset);
    // 3. 写入IDAT块
    offset += iHDRChunk.length;
    pngData.set(iDATChunk, offset);
    // 4. 写入IEND块
    offset += iDATChunk.length;
    pngData.set(iENDChunk, offset);
    
    /* ------ 清空 CRC32 缓存 ------ */
    this.crc32.clear();

    return pngData;
  }
}
